//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useEffect } from 'react';

// *** MUI / STYLING ***
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';

// === API ===
import { removeListener, onTasksAvailable, onUsersAvailable, onUsersActive,
          requestTasksAvailable, requestUsersAvailable, requestUsersActive,
          onServerStartTime, requestServerStartTime, onProkeyWorkerState,
          requestProkeyWorkerState }
  from 'api/prokey/prokey.websocket';
//#endregion

const REFRESH_INTERVAL_MS = 5000;

//-----------------------------------------------------------------
function createStyles(theme) {
  //-----------------------------------------------------------------
  return {
    container: {
      display: 'flex',
      flex: '1 1 auto',
      gap: '1rem',
      height: '100%',
      width: '100%',
    },
    card: {
      flex: '1 1 0',
      minWidth: '25rem',
      minHeight: '19rem',
      height: '100%',
      padding: '0.5rem',
      borderRadius: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    cardContent: {
      maxHeight: '20rem',
      overflowY: 'auto',
    },
    wrapCell: {
      wordBreak: 'break-word',
    },
    labelCell: {
      width: '10rem',
      minWidth: '10rem',
      whiteSpace: 'nowrap',
      verticalAlign: 'top',
    },
    valueCell: {
      width: '100%',
      verticalAlign: 'top',
    },
  };
}

//-----------------------------------------------------------------
function displayRow(label, value, styles, sx = {}) {
  //-----------------------------------------------------------------
  return (
    <TableRow>
      <TableCell sx={styles.labelCell}>{label}</TableCell>
      <TableCell align='right' sx={{ ...styles.valueCell, ...sx }}>{value}</TableCell>
    </TableRow>
  );
}

//-----------------------------------------------------------------
function formatMs(value) {
  //-----------------------------------------------------------------
  if(typeof value !== 'number') return 'none';
  if(value < 1000) return `${value} ms`;
  return `${(value / 1000).toFixed(1)} s`;
}

//-----------------------------------------------------------------
function getWorkerSummary(group) {
  //-----------------------------------------------------------------
  if(!group) return '-';

  const configuredCount = group.configuredCount ?? group.maxCount ?? 0;
  return `${group.total}/${configuredCount} active, ` +
    `${group.busy} busy, ${group.idle} idle, ` +
    `${group.dispatching} dispatching, ${group.pendingTaskRequests || 0} pending, ` +
    `${group.starting} starting`;
}

//-----------------------------------------------------------------
function getAssignedTaskSummary(group) {
  //-----------------------------------------------------------------
  const assignedTasks = group?.workers
    ?.map(worker => worker.assignedTaskId)
    ?.filter(Boolean) || [];

  if(!assignedTasks.length) return '-';
  return assignedTasks.join(', ');
}

//-----------------------------------------------------------------
function getModuleSummary(group) {
  //-----------------------------------------------------------------
  const modules = group?.monitorModules || [];
  if(!modules.length) return '-';
  return modules.join(', ');
}

//-----------------------------------------------------------------
function getTimingValue(group, field) {
  //-----------------------------------------------------------------
  return formatMs(group?.telemetry?.[field]);
}

//-----------------------------------------------------------------
function getWorkerTimingSummary(group) {
  //-----------------------------------------------------------------
  return `dispatch ${getTimingValue(group, 'maxCurrentDispatchActiveMs')} active / ` +
    `${getTimingValue(group, 'maxCurrentIdleBacklogWaitMs')} idle wait / ` +
    `${getTimingValue(group, 'maxLastDispatchWaitMs')} last, ` +
    `task ${getTimingValue(group, 'maxCurrentTaskAgeMs')} current / ` +
    `${getTimingValue(group, 'maxLastTaskDurationMs')} last, ` +
    `finalize ${getTimingValue(group, 'maxLastFinalizeMs')}`;
}

//-----------------------------------------------------------------
function getWorkerRetrySummary(group) {
  //-----------------------------------------------------------------
  const retryCount = group?.telemetry?.retryCount || 0;
  const activeRetryTaskCount = group?.telemetry?.activeRetryTaskCount || 0;
  const lastFailure = group?.telemetry?.lastFailure;
  const summary = `${retryCount} total, ${activeRetryTaskCount} active`;

  if(!retryCount && !lastFailure) return summary;
  if(!lastFailure) return summary;

  return `${summary}; ${lastFailure.taskId || '-'}: ${lastFailure.error || '-'}`;
}

//-----------------------------------------------------------------
function getWorkerExecutionMode(group) {
  //-----------------------------------------------------------------
  return group?.executionMode || '-';
}

//-----------------------------------------------------------------
function ProkeyWorkerCard({ title, group }) {
  //-----------------------------------------------------------------
  const theme = useTheme();
  const styles = createStyles(theme);
  const card = { sx: styles.card, variant: 'primary' };
  const cardContent = { sx: styles.cardContent };

  return (
    <Card {...card}>
      <CardHeader title={title} />
      <CardContent {...cardContent}>
        <Table size='small' width='100%'>
          <TableBody>
            {displayRow('Workers', getWorkerSummary(group), styles)}
            {displayRow('Mode', getWorkerExecutionMode(group), styles)}
            {displayRow('Available Backlog', group?.availableBacklog ?? '-', styles)}
            {displayRow('Modules', getModuleSummary(group), styles, styles.wrapCell)}
            {displayRow('Assigned', getAssignedTaskSummary(group), styles, styles.wrapCell)}
            {displayRow('Timing', getWorkerTimingSummary(group), styles, styles.wrapCell)}
            {displayRow('Retries', getWorkerRetrySummary(group), styles, styles.wrapCell)}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

//-----------------------------------------------------------------
export function ProkeyGeneralPanel() {
  //-----------------------------------------------------------------
  const theme = useTheme();
  const styles = createStyles(theme);
  const card = {
    sx: {
      ...styles.card,
      flex: '1.35 1 34rem',
      minWidth: '32rem',
    },
    variant: 'primary'
  };
  const cardContent = { sx: styles.cardContent };

  const [ server_start,    setServerStart    ] = useState('-');
  const [ tasks_available, setTasksAvailable ] = useState('-');
  const [ users_available, setUsersAvailable ] = useState('-');
  const [ users_prokeying, setUsersProkeying ] = useState('-');
  const [ worker_updated,  setWorkerUpdated  ] = useState('-');

  useEffect(() => {
    function handleServerStartTime(data) {
      setServerStart(data.startTime);
    }

    function handleTasksAvailable(data) {
      setTasksAvailable(data.count);
    }

    function handleUsersAvailable(data) {
      setUsersAvailable(data.count);
    }

    function handleUsersActive(data) {
      setUsersProkeying(data.count);
    }

    function handleProkeyWorkerState() {
      setWorkerUpdated(new Date().toLocaleTimeString());
    }

    function requestGeneralValues() {
      requestServerStartTime();
      requestTasksAvailable();
      requestUsersAvailable();
      requestUsersActive();
    }

    onServerStartTime(handleServerStartTime);
    onTasksAvailable(handleTasksAvailable);
    onUsersAvailable(handleUsersAvailable);
    onUsersActive(handleUsersActive);
    onProkeyWorkerState(handleProkeyWorkerState);

    requestGeneralValues();

    const generalValuesInterval = setInterval(requestGeneralValues, REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(generalValuesInterval);

      removeListener('SERVER-START-TIME', handleServerStartTime);
      removeListener('TASKS-AVAILABLE-COUNT', handleTasksAvailable);
      removeListener('USERS-AVAILABLE-COUNT', handleUsersAvailable);
      removeListener('USERS-ACTIVE-COUNT', handleUsersActive);
      removeListener('PROKEY-WORKER-STATE', handleProkeyWorkerState);
    };
  }, []);

  return (
    <Card {...card}>
      <CardHeader title='General Details' />
      <CardContent {...cardContent}>
        <Table size='small' width='100%'>
          <TableBody>
            {displayRow('Server Start Time', server_start, styles)}
            {displayRow('Tasks Available', tasks_available, styles)}
            {displayRow('Users Keying', users_prokeying, styles)}
            {displayRow('Users Available', users_available, styles)}
            {displayRow('Worker State Updated', worker_updated, styles)}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

//-----------------------------------------------------------------
export function ProkeyStatsPanel() {
  //-----------------------------------------------------------------
  const theme = useTheme();
  const styles = createStyles(theme);
  const container = { sx: styles.container };

  const [ worker_state, setWorkerState ] = useState(null);

  useEffect(() => {
    function handleProkeyWorkerState(data) {
      setWorkerState(data);
    }

    onProkeyWorkerState(handleProkeyWorkerState);

    requestProkeyWorkerState();

    const workerStateInterval = setInterval(requestProkeyWorkerState, REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(workerStateInterval);
      removeListener('PROKEY-WORKER-STATE', handleProkeyWorkerState);
    };
  }, []);

  return (
    <Box {...container}>
      <ProkeyWorkerCard title='Prokey Auto' group={worker_state?.auto} />
      <ProkeyWorkerCard title='Prokey Silent' group={worker_state?.silent} />
    </Box>
  );
}
